import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, PLATFORM_ID, afterNextRender, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Same-day cut-off countdown on the homepage hero (Figma Wireframe_v2 "LIVE · BROOKLYN STOCKROOM").
 *  Counts down to the 2 PM ET cut-off on business days (Mon-Fri); after it, or at the weekend,
 *  to the next business day's cut-off. Holiday closures are not considered: their dates are
 *  banner dates, not the closed days (see HolidayClosureService).
 *
 *  Browser-only on purpose: server.ts caches page HTML for up to an hour, so a server-rendered
 *  time would be wrong. The server ships "--:--:--" and the clock starts after the first render. */

const ZONE = 'America/New_York';
const OPEN_SEC = 7 * 3600;     // 7 AM: left end of the track
const CUTOFF_SEC = 14 * 3600;  // 2 PM cut-off
const DAY_SEC = 86400;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

@Component({
  selector: 'app-cutoff-card',
  standalone: true,
  templateUrl: './cutoff-card.component.html',
  styleUrl: './cutoff-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CutoffCardComponent implements OnDestroy {
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private timer: ReturnType<typeof setInterval> | null = null;
  private parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE, weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });

  remaining = '--:--:--';
  /** "today", "tomorrow" or a weekday name: when an order placed now ships. */
  shipDay = 'today';
  /** 0-1 along the 7 AM to 2 PM part of the track. */
  progress = 0;

  constructor() {
    afterNextRender(() => {
      if (!this.isBrowser) return;
      this.tick();
      // setInterval inside the zone keeps the app from ever becoming stable (NG0506).
      this.zone.runOutsideAngular(() => {
        this.timer = setInterval(() => this.zone.run(() => this.tick()), 1000);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  private tick(): void {
    const p = Object.fromEntries(this.parts.formatToParts(new Date()).map(x => [x.type, x.value]));
    const day = WEEKDAYS.indexOf(p['weekday']);
    const now = +p['hour'] * 3600 + +p['minute'] * 60 + +p['second'];
    const isBusinessDay = (d: number) => d >= 1 && d <= 5;

    let ahead = 0;
    if (!isBusinessDay(day) || now >= CUTOFF_SEC) {
      ahead = 1;
      while (!isBusinessDay((day + ahead) % 7)) ahead++;
    }

    const left = CUTOFF_SEC - now + ahead * DAY_SEC;
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    const s = left % 60;
    this.remaining = [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
    this.shipDay = ahead === 0 ? 'today' : ahead === 1 ? 'tomorrow' : DAY_NAMES[(day + ahead) % 7];
    this.progress = ahead === 0 ? Math.min(1, Math.max(0, (now - OPEN_SEC) / (CUTOFF_SEC - OPEN_SEC))) : 0;
    this.cdr.markForCheck();
  }
}
